import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, GeoJSON, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useCentre } from "@/context/CentreContext";

const CITIES = [["Paris", 48.857, 2.352], ["Lyon", 45.764, 4.836], ["Bordeaux", 44.838, -0.579], ["Lille", 50.63, 3.057],
  ["Nantes", 47.218, -1.554], ["Toulouse", 43.605, 1.444], ["Strasbourg", 48.573, 7.752], ["Rennes", 48.117, -1.678],
  ["Nice", 43.71, 7.262], ["Clermont-Ferrand", 45.777, 3.087], ["Dijon", 47.322, 5.041]];
const MINOR = ["Clermont-Ferrand", "Dijon", "Rennes", "Strasbourg"];

const dot = (mother) => L.divIcon({ className: "", html: `<span class="hp-map-dot${mother ? " hp-map-dot--mother" : ""}"></span>`, iconSize: [22, 22], iconAnchor: [11, 11] });
const label = (name, cls) => L.divIcon({ className: "", html: `<span class="${cls}">${name}</span>`, iconSize: [0, 0] });

const STYLE_EU = { color: "#3a393d", weight: 1, fillColor: "#1b1a1d", fillOpacity: 1 };
const STYLE_FR = { color: "#4a494e", weight: 1, fillColor: "#262529", fillOpacity: 1 };

const FRANCE = [[42.3, -4.8], [51.1, 8.2]];

function FitFrance() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    let w = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === w) return;
      w = el.clientWidth;
      map.invalidateSize();
      map.fitBounds(FRANCE);
    });
    ro.observe(el);
    map.invalidateSize();
    map.fitBounds(FRANCE);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function useGeo(url) {
  const [d, setD] = useState(null);
  useEffect(() => { fetch(url).then((r) => r.json()).then(setD); }, [url]);
  return d;
}

export default function CentresMap({ height = "h-[460px] sm:h-[600px]" }) {
  const { centres } = useCentre();
  const eu = useGeo("/geo/europe.json");
  const fr = useGeo("/geo/france-regions.json");
  const pts = centres.filter((c) => c.lat && c.lng);
  return (
    <div className={`hp-map relative isolate z-0 ${height} border border-white/10 overflow-hidden`} data-testid="centres-map">
      <MapContainer bounds={FRANCE} minZoom={4} maxZoom={9} zoomSnap={0.25} scrollWheelZoom={false}
        maxBounds={[[38, -12], [55, 16]]} className="h-full w-full" attributionControl={false}>
        <FitFrance />
        {eu && <GeoJSON data={eu} style={(f) => (f.properties.iso === "FR" ? { ...STYLE_EU, fillOpacity: 0 } : STYLE_EU)} interactive={false} />}
        {fr && <GeoJSON data={fr} style={STYLE_FR} interactive={false} />}
        {CITIES.map(([n, la, lo]) => <Marker key={n} position={[la, lo]} icon={label(n, `hp-map-city${MINOR.includes(n) ? " hp-map-city--minor" : ""}`)} interactive={false} />)}
        {pts.map((c) => (
          <Marker key={c.slug} position={[c.lat, c.lng]} icon={dot(c.is_mother)}>
            <Popup>
              <div data-testid={`map-popup-${c.slug}`}>
                <p className="font-display font-extrabold uppercase text-sm text-white">{c.name}</p>
                {c.is_mother && <p className="text-[10px] font-bold uppercase tracking-wider text-hp-gold">Maison mère</p>}
                <p className="text-xs mt-1 text-neutral-300">{c.address}{c.address ? ", " : ""}{c.postal_city}</p>
                <p className="text-xs mt-1 text-neutral-300">{c.phone}</p>
                <Link to={`/centres/${c.slug}`} className="inline-block mt-2 text-xs font-bold uppercase !text-hp-yellow">Voir le centre →</Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <p className="absolute bottom-1 right-2 z-[400] text-[10px] text-neutral-600 pointer-events-none">Données : Natural Earth · Etalab</p>
    </div>
  );
}

export function NetworkMapSection() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="network-map-section">
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-10">
        Un réseau proche de <span className="text-hp-yellow underline decoration-hp-yellow/60 decoration-4 underline-offset-8">chez vous</span>
      </h2>
      <CentresMap />
    </section>
  );
}
